import React from "react";
import {
  Card,
  CardHeader,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Body1,
  Link,
  Button,
  Tooltip,
  makeStyles,
  makeStaticStyles,
  Popover,
  PopoverSurface,
  PopoverTrigger,
} from "@fluentui/react-components";
import { ArrowEjectRegular, Dismiss24Regular, Info20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import globalStyles from "./CommonStyle";

// Define custom styles using makeStyles
const useStyles = makeStyles({
  root: {
    borderRadius: "0em !important",
    boxShadow: "none",
    paddingTop: "0px",
    marginBottom: "12px",
    paddingBottom: "0px",
  },
  dailogActionContainer: {
    flexDirection: "row",
    marginLeft: "auto",
  },
  dailogButton: {
    width: "49px",
    height: "32px",
    minWidth: "49px",
    fontWeight: "normal",
  },
  dailogButtonYes: {
    width: "49px",
    height: "32px",
    minWidth: "49px",
    marginLeft: "12px",
    backgroundColor: "#278838", // Green background
    ":hover": {
      backgroundColor: "#1f6d2d", // Slightly darker green on hover
      color: "#fff",
    },
    fontWeight: "normal",
  },
  dailogContainer: {
    margin: "auto 10px auto 10px",
  },
  dialogTitle: {
    fontSize: "16px",
    fontWeight: "semibold",
    color: "#001737",
  },
  dialogContent: {
    fontSize: "14px",
    color: "#546176",
    fontWeight: "regular",
    paddingBottom: "12px",
    borderBottom: "1px solid #dee1ea",
  },
});

const StaticCopyCard: React.FC = () => {
  const [isTooltipVisible, setIsTooltipVisible] = React.useState(false); // State for tooltip visibility
  const buttonRef = React.useRef<HTMLButtonElement>(null); // Ref for the button
  const classes = useStyles();
  const globalStyle = globalStyles();

  // Handle clicks outside the button to close the tooltip
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsTooltipVisible(false);
      }
    };

    if (isTooltipVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTooltipVisible]);

  const { t } = useTranslation();

  const statifyWorkbook = async () => {
    try {
      await Excel.run(async (context) => {
        const sheetsCollection = context.workbook.worksheets;
        sheetsCollection.load(["items"]);
        await context.sync();
        const sheets = sheetsCollection.items;

        for (let sheet of sheets) {
          await statifySheet(sheet, context);
        }

        return context.sync();
      });
    } catch (error) {
      console.error("Statify error: ", error);
    }
  };

  const statifySheet = async (sheet: Excel.Worksheet, context: Excel.RequestContext) => {
    try {
      // Find all cells which contains the namespace CLAUSION
      const possibleFunctionCells = sheet.findAll("CLAUSION.", {
        completeMatch: false,
        matchCase: false,
      });

      possibleFunctionCells.load(["address", "cellCount", "text", "worksheet", "areas", "values"]);
      await context.sync().then(function () {
        console.log("Event handler successfully registered.");
      });

      possibleFunctionCells.areas.items.forEach((range) => {
        const possibleFormula = range.formulas;
        if (possibleFormula[0][0][0] !== "=" && possibleFormula[0][0][0] !== "@") {
          // Check if actually a function
          return;
        }
        statifyCell(range);
      });
    } catch (error) {
      if (error.code !== Excel.ErrorCodes.itemNotFound) {
        console.error(error.code, error.message, " INFO: ", error.debugInfo);
      }
    }
  };

  const statifyCell = async (cell: Excel.Range) => {
    let statified: string = cell.values[0][0];
    cell.formulas = [[`${statified}`]];
  };

  const convert_workbook_to_static = () => {
    statifyWorkbook();
  };

  return (
    <Card className={classes.root}>
      <CardHeader
        header={
          <Body1 className={globalStyle.headerContainer}>
            {/* Group the icon and title together */}
            <span className={globalStyle.titleContainer}>
              <ArrowEjectRegular />
              <span className={`${globalStyle.fontStyle},${globalStyle.font14}`}>{t("staticcopy_title")}</span>
            </span>
            <Popover>
              <PopoverTrigger disableButtonEnhancement>
                <Button
                  ref={buttonRef} // Attach ref to the button
                  appearance="transparent"
                  icon={<Info20Regular />}
                  size="small" // Reduce button size
                  onClick={() => setIsTooltipVisible(!isTooltipVisible)} // Toggle tooltip visibility
                />
              </PopoverTrigger>

              <PopoverSurface className={globalStyle.popoverSurface}>
                <div>
                  <p>
                    {t("staticcopy_text")}{" "}
                  </p>
                </div>
              </PopoverSurface>
            </Popover>
          </Body1>
        }
      />
      <Dialog>
        <DialogTrigger disableButtonEnhancement>
          <Button className={globalStyle.font14}>{t("staticcopy_button")}</Button>
        </DialogTrigger>
        <DialogSurface className={classes.dailogContainer}>
          <DialogBody>
            <DialogTitle
              className={classes.dialogTitle}
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              {t("staticcopy_dialog_title")}
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>{t("staticcopy_dialog_content")}</DialogContent>
            <DialogActions>
              <div className={classes.dailogActionContainer}>
                <DialogTrigger disableButtonEnhancement>
                  <Button className={classes.dailogButton} appearance="secondary">
                    {t("staticcopy_close_button")}
                  </Button>
                </DialogTrigger>
                <DialogTrigger disableButtonEnhancement>
                  <Button
                    className={classes.dailogButtonYes}
                    appearance="primary"
                    onClick={() => convert_workbook_to_static()}
                  >
                    {t("staticcopy_confirm_button")}
                  </Button>
                </DialogTrigger>
              </div>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </Card>
  );
};

export default StaticCopyCard;
